import {createDuplicateValidator} from './../../shared/utils/duplicate-fc-validator';
import {MessageService} from './../../services/message.service';
import {
  CreateTeamAction,
  GetTeamsAction,
  UpdateTeamAction,
  DeleteTeamAction,
} from './../../core/store/teams/teams.action';
import {ChangeDetectionStrategy, Component, inject, OnInit} from '@angular/core';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {Store} from '@ngrx/store';

import {OphDialogFooterComponent} from './../../shared/design/oph-dialog-footer/oph-dialog-footer.component';
import {OphInputOrangeComponent} from './../../shared/design/oph-inputs/oph-input-orange/oph-input-orange.component';
import {Team} from '../shared/model/team';

export type TeamDialogData = {
  team: Team;
  teams?: Team[];
};

@Component({
  selector: 'team-dialog',
  standalone: true,
  imports: [OphInputOrangeComponent, FormsModule, ReactiveFormsModule, OphDialogFooterComponent, MatIconModule],
  templateUrl: './team-dialog.component.html',
  styleUrl: './team-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamDialogComponent implements OnInit {
  readonly data = inject<TeamDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<TeamDialogData>);
  private messageService = inject(MessageService);
  private store$ = inject(Store);
  private fb = inject(FormBuilder);

  private readonly duplicateRoleValidator = createDuplicateValidator((value: string) => {
    if (!this.data.teams) return false;
    const displayName = value.toLowerCase();
    return this.data.teams.some(team => team.name.toLowerCase() === displayName && team.id !== this.data.team?.id);
  });

  public teamFg = this.fb.group({
    id: this.data.team?.id || null,
    name: [this.data.team?.name || '', [Validators.required, this.duplicateRoleValidator]],
  });

  public menuOptions = ['delete'];

  isLoading = false;
  teamHasId = false;

  ngOnInit(): void {
    this.teamHasId = !!this.data.team?.id;
    this.teamFg = this.fb.group({
      id: this.data.team?.id || null,
      name: [this.data.team?.name || '', [Validators.required, this.duplicateRoleValidator]],
    });
  }

  footerActionLabels() {
    return {
      cancel: 'Cancel',
      save: this.teamHasId ? 'Update Team' : 'Create Team',
    };
  }

  onSave() {
    if (this.teamHasId) {
      this.updateTeam();
      return;
    }
    this.createTeam();
  }

  onCancel() {
    this.dialogRef.close();
  }

  onMenuOption(type: (typeof this.menuOptions)[number]) {
    if (type === 'delete') {
      this.deleteTeam();
    }
  }

  createTeam(): void {
    if (this.teamFg.invalid) {
      this.teamFg.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    this.store$.dispatch(
      new CreateTeamAction({
        team: this.teamFg.value as Team,
        onSuccess: () => {
          this.isLoading = false;
          this.dialogRef.close(this.teamFg.value);
          this.messageService.add(`Team created successfully`, 'newTaskAssigned');
          this.store$.dispatch(new GetTeamsAction({}));
        },
        onFailure: error => {
          this.isLoading = false;
          this.messageService.add(`Error creating team`, 'error');
          console.error('Error creating team:', error);
        },
      })
    );
  }

  updateTeam(): void {
    if (this.teamFg.invalid) {
      this.teamFg.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    this.store$.dispatch(
      new UpdateTeamAction({
        team: this.teamFg.value as Team,
        onSuccess: () => {
          this.isLoading = false;
          this.dialogRef.close(this.teamFg.value);
          this.messageService.add(`Team updated successfully`, 'newTaskAssigned');
          this.store$.dispatch(new GetTeamsAction({}));
        },
        onFailure: error => {
          this.isLoading = false;
          this.messageService.add(`Error updating team`, 'error');
          console.error('Error updating team:', error);
        },
      })
    );
  }

  deleteTeam() {
    this.isLoading = true;

    this.store$.dispatch(
      new DeleteTeamAction({
        id: this.data.team?.id,
        onSuccess: () => {
          this.isLoading = false;
          this.dialogRef.close(this.teamFg.value);
          this.messageService.add(`Team deleted successfully`, 'newTaskAssigned');
          this.store$.dispatch(new GetTeamsAction({}));
        },
        onFailure: error => {
          this.isLoading = false;
          this.messageService.add(`Error updating team`, 'error');
          console.error('Error updating team:', error);
        },
      })
    );
  }
}
